import type { IObservableArray } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import type { Creative, DisplayCreative } from '@feathr/blackbox';
import { EmptyState, Table } from '@feathr/components';
import Page from '@feathr/extender/App/Page';
import AddCreativeButton from '@feathr/extender/components/creatives/AddCreativeButton';
import { AdDimensionsSelect } from '@feathr/extender/components/creatives/AdDimensionsSelect';
import CreativeModal from '@feathr/extender/components/creatives/CreativeModal';
import { useLocalUrl, useStore } from '@feathr/extender/state';

import ProjectChannelSelect from './ProjectChannelSelect';
import ProjectCreativesColumns from './ProjectCreativesColumns';
import { useProjectCreativesPage } from './ProjectCreativesPage.hooks';
import ProjectCreativeTypeSelect from './ProjectCreativeTypeSelect';
import ProjectNameSearch from './ProjectNameSearch';

import * as styles from './ProjectCreativesPage.css';

function ProjectCreativesPage(): JSX.Element {
  const { t } = useTranslation();
  const { eventId } = useParams<{ eventId: string }>();

  const {
    filters,
    setFilters,
    editingCreative,
    handleEditCreative,
    handleCloseEditModal,
    tableFilters,
    hasActiveFilters,
    selectedChannel,
    handleDimensionsChange,
    handleDimensionsClear,
  } = useProjectCreativesPage({ eventId });

  const { Campaigns, Creatives } = useStore();
  const localUrl = useLocalUrl();

  const creativesResponse = Creatives.list({
    filters: tableFilters,
  });

  const filterElements = [
    <React.Fragment key={'channel-filter'}>
      <ProjectChannelSelect filters={filters} setFilters={setFilters} />
      <ProjectCreativeTypeSelect filters={filters} setFilters={setFilters} />
      <AdDimensionsSelect
        channel={selectedChannel}
        isClearable={true}
        isSearchable={false}
        onChange={handleDimensionsChange}
        onClear={handleDimensionsClear}
        value={filters.spec}
        wrapperClassName={styles.dimensionsFilter}
      />
    </React.Fragment>,
    <ProjectNameSearch filters={filters} key={'name-search'} setFilters={setFilters} />,
  ];

  const actions = (
    <AddCreativeButton
      creatives={creativesResponse.models as IObservableArray<DisplayCreative>}
      eventId={eventId}
    />
  );

  /*
   * Only show the EmptyState when the project truly has no creatives (no filters applied).
   * If filters are applied but produce no results, show the Table with its built-in empty state
   * so users can see and adjust their filters.
   */
  const showEmptyState =
    creativesResponse.isLoaded && creativesResponse.models.length === 0 && !hasActiveFilters;

  return (
    <Page
      actions={actions}
      contentClassName={showEmptyState ? styles.fullHeightContent : undefined}
      title={t('Creatives')}
    >
      {showEmptyState ? (
        <EmptyState
          description={t('Get started by adding a new creative to this project.')}
          height={'full'}
          label={t('No creatives')}
          theme={'white'}
          width={'full'}
        >
          <AddCreativeButton
            creatives={creativesResponse.models as IObservableArray<DisplayCreative>}
            eventId={eventId}
            type={'secondary'}
          />
        </EmptyState>
      ) : (
        <Table<Creative>
          collection={Creatives}
          columns={ProjectCreativesColumns({
            Campaigns,
            localUrl,
            onEditCreative: handleEditCreative,
          })}
          filterElements={filterElements}
          filters={tableFilters}
          initialSort={[{ id: 'date_last_modified', desc: true }]}
        />
      )}
      {editingCreative && (
        <CreativeModal creative={editingCreative} onClose={handleCloseEditModal} opened={true} />
      )}
    </Page>
  );
}

export default observer(ProjectCreativesPage);
